/**
 * Sensors API — fleet health, geo map, registry, maintenance queues, the
 * reporting matrix, data-quality stats, and per-sensor detail.
 *
 * Types mirror `dashboard_api/features/sensors/models.py` field-for-field.
 * Identity fields (`account_no`, `meter_no`, `ward_no`, `device_name`, …) come
 * from the utility's asset system and are absent on the synthetic estate — the
 * UI hides a block whose fields are all empty rather than showing dashes.
 */
import { http } from "@/lib/api";
import type { SeverityTier } from "@/lib/severity";
import type { SensorState } from "@/lib/status";

/** Reporting status (`SENSOR_STATES`); widened to `string` for forward compatibility. */
export type SensorStatus = SensorState | string;

/**
 * The anomaly flags (`boond_contracts.device_flags.ANOMALY_FLAGS`). `empty_pipe`
 * is not one: under intermittent supply it is expected between supply windows,
 * so the API reports it apart, as `SensorDetail.no_flow_readings`.
 */
export interface AnomalyFlags {
  leakage: boolean;
  burst: boolean;
  reverse_flow: boolean;
  over_range: boolean;
  over_temperature: boolean;
}

export type AnomalyKind = keyof AnomalyFlags;

/** Render order for anomaly chips — severity-descending, as the reference app. */
export const ANOMALY_KINDS: AnomalyKind[] = [
  "leakage",
  "burst",
  "reverse_flow",
  "over_range",
  "over_temperature",
];

export type AnomalyCounts = Record<AnomalyKind, number>;

export interface SensorGeoPoint {
  sensor_id: string;
  house_id: string | null;
  device_name: string | null;
  locality: string | null;
  user_type: string | null;
  user_subtype: string | null;
  latitude: number;
  longitude: number;
  status: SensorStatus;
  last_seen: string | null;
  last_battery: number | null;
  hours_since_report: number | null;
  coverage_pct: number;
  zero_pct: number;
  max_null_streak: number;
  last_seen_slots: number;
}

export interface SensorStatusSummary {
  active: number;
  zero_report: number;
  irregular: number;
  inactive: number;
  long_term_offline: number;
  total: number;
}

// Reuses the four state counts rather than restating them, but drops `total`:
// /sensors/health-summary names that field `total_sensors` and never sends
// `total`. Inheriting it declared a field the API does not return, so
// `data.total` typechecked and was undefined at runtime.
export interface SensorHealthSummary extends Omit<SensorStatusSummary, "total"> {
  total_sensors: number;
  /** "Meters reporting": seen within `active_hours` of the data horizon. A
   * point-in-time connectivity count — not the `active` health grade.
   * `missing_data_count` is its exact complement. */
  reporting_sensors: number;
  reporting_sensors_pct: number;
  health_pct: number;
  low_battery_count: number;
  missing_data_count: number;
  total_anomalies: number;
  requires_maintenance: number;
  analysis_days: number;
  as_of: string | null;
}

export interface SensorRegistryRow {
  sensor_id: string;
  house_id: string | null;
  device_name: string | null;
  customer_name: string | null;
  sensor_model: string | null;
  firmware_version: string | null;
  locality: string | null;
  user_type: string | null;
  user_subtype: string | null;
  area_marla: number | null;
  address: string | null;
  account_no: string | null;
  meter_no: string | null;
  ward_no: string | null;
  sub_division: string | null;
  facility_type: string | null;
  latitude: number | null;
  longitude: number | null;
  status: SensorStatus;
  asset_status: string | null;
  installed: string | null;
  last_seen: string | null;
  last_battery: number | null;
}


export interface LowBatterySensor {
  sensor_id: string;
  house_id: string | null;
  device_name: string | null;
  locality: string | null;
  battery_level: number;
  battery_alarm: boolean;
  last_seen: string | null;
  /** The one action scale (urgent / investigate / advisory), graded server-side. */
  priority: SeverityTier;
}

export interface MissingDataSensor {
  sensor_id: string;
  house_id: string | null;
  device_name: string | null;
  locality: string | null;
  last_seen: string | null;
  hours_since: number | null;
  days_offline: number | null;
  battery_level: number | null;
  /** The one action scale (urgent / investigate / advisory), graded server-side. */
  priority: SeverityTier;
  /** Normal daily consumption the silence leaves unmeasured — what `priority` grades. */
  baseline_m3_per_day: number | null;
  baseline_as_of: string | null;
  action: string;
}

export interface MaintenanceItem {
  sensor_id: string;
  house_id: string | null;
  device_name: string | null;
  locality: string | null;
  issue: string;
  action: string;
  /** The one action scale (urgent / investigate / advisory), graded server-side. */
  priority: SeverityTier;
  details: string;
  days_offline: number | null;
  last_seen: string | null;
  /** Only set for a "Low Battery" row — lets `details` be rebuilt in the active language. */
  battery_level: number | null;
}

/** Matrix cell state: 2 reported with flow · 1 reported, no flow · 0 missing · -1 confirmed outage. */
export type MatrixState = 2 | 1 | 0 | -1;

/**
 * Row order applied server-side (`dashboard_api/features/sensors/clustering.py`):
 * "off" is the requested `sort`; "within_locality"/"global" order rows by
 * reporting-pattern similarity (hierarchical clustering) instead — the former
 * within each locality band, the latter across all rows regardless of locality.
 */
export type ClusterMode = "off" | "within_locality" | "global";

export interface HealthMatrix {
  days: string[];
  sensors: string[];
  labels: string[];
  localities: (string | null)[];
  states: MatrixState[][];
  coverage_pct: number[];
  max_null_streak: number[];
  /** Each row's sensor status (`SENSOR_STATES`) — the one the map states for that
   * sensor. `null` only for a row whose sensor is not in the registered fleet. */
  statuses: (string | null)[];
  /** Status counts over the whole registered fleet, not only the rendered rows. */
  status_summary: SensorStatusSummary;
  /** Empty when `cluster === "global"` — bands aren't contiguous there. */
  locality_starts: number[];
  locality_labels: string[];
  total_sensors: number;
  reporting_pct: number;
  as_of: string | null;
  cluster: ClusterMode;
}

export interface QualityStats {
  total_sensors: number;
  validity_pct: number;
  timeliness_pct: number;
  as_of: string;
}

export interface ReadingPoint {
  timestamp: string;
  cumulative_volume_m3: number;
  consumption_delta_m3: number | null;
  battery_level: number | null;
  supply_window: string | null;
  leakage: boolean;
  burst: boolean;
}

export interface DailyConsumptionPoint {
  day: string;
  consumption_m3: number;
  reading_count: number;
}

export interface SensorDetail {
  sensor_id: string;
  house_id: string | null;
  device_name: string | null;
  sensor_model: string | null;
  firmware_version: string | null;
  device_external_id: string | null;
  customer_name: string | null;
  mobile_no: string | null;
  /** Masked server-side unless the caller holds the `admin` role. */
  cnic: string | null;
  cnic_masked: boolean;
  account_no: string | null;
  meter_no: string | null;
  ward_no: string | null;
  sub_division: string | null;
  facility_type: string | null;
  commercial_type: string | null;
  address: string | null;
  locality: string | null;
  user_type: string | null;
  user_subtype: string | null;
  area_marla: number | null;
  tariff: string | null;
  latitude: number | null;
  longitude: number | null;
  installed: string | null;
  last_maintenance: string | null;
  asset_status: string | null;
  status: SensorStatus;
  last_seen: string | null;
  hours_since_report: number | null;
  last_battery: number | null;
  battery_reported: boolean;
  battery_alarm: boolean;
  current_volume_m3: number | null;
  period_days: number;
  period_start: string | null;
  period_end: string | null;
  period_consumption_m3: number;
  previous_period_consumption_m3: number;
  consumption_change_percent: number;
  data_quality_percent: number;
  reading_count: number;
  reading_count_capped: number;
  expected_readings: number;
  expected_daily_readings: number;
  coverage_pct: number;
  max_null_streak: number;
  has_current_anomaly: boolean;
  current_anomalies: AnomalyFlags;
  period_anomalies: AnomalyCounts;
  anomaly_count: number;
  /** Readings with no flow (`empty_pipe`) — informational, never an anomaly. */
  no_flow_readings: number;
  daily_consumption: DailyConsumptionPoint[];
  history: ReadingPoint[];
}

async function get<T>(url: string, params?: object, signal?: AbortSignal): Promise<T> {
  const { data } = await http.get<T>(url, { params, signal });
  return data;
}

/** Include `locality` in the query only when a real locality is selected. */
const loc = (locality?: string | null) => (locality ? { locality } : {});

export const fetchSensorHealthSummary = (
  days: number,
  locality?: string | null,
  s?: AbortSignal,
) => get<SensorHealthSummary>("/sensors/health-summary", { days, ...loc(locality) }, s);

export const fetchSensorGeoHealth = (days: number, locality?: string | null, s?: AbortSignal) =>
  get<SensorGeoPoint[]>("/sensors/geo-health", { days, ...loc(locality) }, s);

export const fetchSensorRegistry = (days: number, locality?: string | null, s?: AbortSignal) =>
  get<SensorRegistryRow[]>("/sensors/registry", { days, ...loc(locality) }, s);

export const fetchLowBattery = (
  threshold: number,
  locality?: string | null,
  s?: AbortSignal,
) => get<LowBatterySensor[]>("/sensors/low-battery", { threshold, ...loc(locality) }, s);

export const fetchMissingData = (hours: number, locality?: string | null, s?: AbortSignal) =>
  get<MissingDataSensor[]>("/sensors/missing-data", { hours, ...loc(locality) }, s);

export const fetchMaintenance = (locality?: string | null, s?: AbortSignal) =>
  get<MaintenanceItem[]>("/sensors/maintenance", loc(locality), s);

export const fetchHealthMatrix = (
  days: number,
  locality?: string | null,
  sort: "locality" | "health" = "locality",
  cluster: ClusterMode = "off",
  s?: AbortSignal,
) => get<HealthMatrix>("/sensors/health-matrix", { days, sort, cluster, ...loc(locality) }, s);

export const fetchQualityStats = (days: number, locality?: string | null, s?: AbortSignal) =>
  get<QualityStats>("/sensors/quality-stats", { days, ...loc(locality) }, s);

export const fetchSensorDetail = (id: string, days: number, s?: AbortSignal) =>
  get<SensorDetail>(`/sensors/${encodeURIComponent(id)}`, { days }, s);
